import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  WorkOutline as WorkOutlineIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import {
  Contact,
  ContactInteraction,
  OutreachLogEntry,
  contactsApi,
  contactInteractionsApi,
  outreachLogApi,
} from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { formatPhoneInput, formatPhoneNumber, isValidPhone, toE164 } from '../../utils/formatPhone'
import {
  CONNECTION_TYPE_OPTIONS,
  CONTACT_STATUS_OPTIONS,
  CONTACT_STATUS_UNSET_LABEL,
  DEFAULT_CONNECTION_TYPE,
  DEFAULT_INTERACTION_TYPE,
  INTERACTION_TYPE_OPTIONS,
  formatReachOutAt,
  interactionTypeLabel,
} from '../../constants/networking'
import { toDateTimeLocalInput, todayLocalYMD } from '../../utils/dateFormat'
import PanelSection from '../common/PanelSection'
import OutreachChecklist from './OutreachChecklist'
import OutreachComposer from './OutreachComposer'
import { recipientFromContact } from '../../utils/outreachRecipient'
import { buildContactActivity, followUpState, type FollowUpState } from './contactActivity'
import ResponsiveSheet from '../common/ResponsiveSheet'

interface ContactDetailPanelProps {
  contact: Contact | null
  open: boolean
  onClose: () => void
  onUpdate: (contact: Contact) => void
  onDelete: (id: string) => void
  onAddReferral?: (referredByContactId: string, referredByName: string) => void
  /**
   * MWA-297 — this contact told them about a *job*, not a person. Distinct from
   * onAddReferral, which adds a referred contact. Optional so a host that
   * cannot create opportunities simply renders no action.
   */
  onAddReferredOpportunity?: (contactId: string, contactName: string) => void
  clientTargetRole?: string
  /** Whose network this contact sits in. Staff-only — see the header comment. */
  clientName?: string
  readOnly?: boolean
  /**
   * Whose side of the panel this is — the same vocabulary
   * NetworkingContactsTable already uses, rather than a second mechanism for
   * the same distinction. Staff author the reach-out instruction; a client
   * only reads it, which update-contact enforces server-side regardless.
   */
  audience: 'staff' | 'client'
}

// Statuses that should trigger the "Did this contact refer you to anyone?" prompt.
// Whitelist (not blacklist): future statuses default to "no prompt" unless explicitly added.
// Only 'contacted' qualifies now — a referral can only come from someone who
// actually replied, which 'pending' does not claim and 'not_comfortable' denies.
const STATUSES_PROMPTING_REFERRAL_CHECK = new Set(['contacted'])

type PanelView = 'record' | 'outreach'

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

const FOLLOW_UP_CHIP: Record<
  FollowUpState,
  { color: 'warning' | 'error' | 'success' | 'default'; label: (e: OutreachLogEntry) => string }
> = {
  due: { color: 'warning', label: (e) => `Follow up due ${shortDate(e.followUpDueAt)}` },
  overdue: { color: 'error', label: (e) => `Follow up overdue, was due ${shortDate(e.followUpDueAt)}` },
  completed: { color: 'success', label: () => 'Followed up' },
  dismissed: { color: 'default', label: () => 'Dismissed' },
}

export default function ContactDetailPanel({
  contact,
  open,
  onClose,
  onUpdate,
  onDelete,
  onAddReferral,
  onAddReferredOpportunity,
  clientTargetRole,
  clientName,
  readOnly,
  audience,
}: ContactDetailPanelProps) {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const { showError, showSuccess } = useNotification()
  const [interactions, setInteractions] = useState<ContactInteraction[]>([])
  const [loadingInteractions, setLoadingInteractions] = useState(true)
  const [interactionsError, setInteractionsError] = useState(false)
  // Follow-up state for the outreach sent to this contact, folded into Activity.
  // Not loaded for read-only viewers, who never saw it.
  const [outreachEntries, setOutreachEntries] = useState<OutreachLogEntry[]>([])
  const [followUpsError, setFollowUpsError] = useState(false)
  const [savingFollowUpId, setSavingFollowUpId] = useState<string | null>(null)
  const [newInteractionType, setNewInteractionType] = useState<string>(DEFAULT_INTERACTION_TYPE)
  const [newInteractionNotes, setNewInteractionNotes] = useState('')
  // MWA-297 — the date the conversation happened. Prefilled with today so the
  // common case is unchanged; a staff member catching up can move it back.
  const [newInteractionDate, setNewInteractionDate] = useState(todayLocalYMD())
  const [saving, setSaving] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [referralPromptOpen, setReferralPromptOpen] = useState(false)
  const [view, setView] = useState<PanelView>('record')
  const [reachOutAtInput, setReachOutAtInput] = useState('')
  const [reachOutReasonInput, setReachOutReasonInput] = useState('')
  const [savingInstruction, setSavingInstruction] = useState(false)
  const [instructionError, setInstructionError] = useState('')
  const [phoneInput, setPhoneInput] = useState('')

  const contactId = contact?.id

  // Only reload the interaction timeline when the *selected* contact changes,
  // not on every field mutation (which would re-skeleton the panel on each
  // checklist tick, status change, etc.). A failure used to be swallowed into
  // console.error, after which the Activity section claimed "No activity yet"
  // — a different statement from "we could not load it".
  const loadInteractions = useCallback(() => {
    if (!contactId) return
    setLoadingInteractions(true)
    setInteractionsError(false)
    contactInteractionsApi
      .list({ contactId })
      .then((rows) => {
        setInteractions(rows)
        setInteractionsError(false)
      })
      .catch((error) => {
        console.error('Failed to load contact interactions:', error)
        setInteractionsError(true)
      })
      .finally(() => setLoadingInteractions(false))
  }, [contactId])

  useEffect(() => {
    loadInteractions()
  }, [loadInteractions])

  // No skeleton: Activity renders from the interactions, and the follow-up
  // chips join their rows when this lands. A failure leaves the list intact
  // and says the follow-ups are missing. Rows are filtered to this contact so
  // a response that lands after the panel has moved on cannot show the wrong
  // person's follow-ups.
  const loadOutreach = useCallback(() => {
    if (!contactId || readOnly) return
    outreachLogApi
      .list({ contactId })
      .then((rows) => {
        setOutreachEntries((rows || []).filter((r) => r.contactId === contactId))
        setFollowUpsError(false)
      })
      .catch((error) => {
        console.error('Failed to load outreach follow-ups:', error)
        setFollowUpsError(true)
      })
  }, [contactId, readOnly])

  useEffect(() => {
    setOutreachEntries([])
    setFollowUpsError(false)
    loadOutreach()
  }, [loadOutreach])

  const handleOutreachChanged = useCallback(() => {
    loadInteractions()
    loadOutreach()
  }, [loadInteractions, loadOutreach])

  const handleFollowUp = async (id: string, status: 'completed' | 'dismissed') => {
    setSavingFollowUpId(id)
    try {
      await outreachLogApi.updateFollowUp(id, status)
      showSuccess(status === 'completed' ? 'Marked as followed up' : 'Follow-up dismissed')
      loadOutreach()
    } catch (err) {
      console.error('Failed to update follow-up:', err)
      showError('Failed to update follow-up status')
    } finally {
      setSavingFollowUpId(null)
    }
  }

  const activityRows = buildContactActivity(interactions, outreachEntries)

  // The panel is not unmounted when it closes, so the view it was left on
  // would still be showing the next time it opens.
  useEffect(() => {
    if (open) setView('record')
  }, [open, contactId])

  // Mirror the stored instruction into the staff form. Re-syncs after a save
  // (onUpdate hands back the saved contact) but not after a failure, so a
  // rejected save leaves the strategist's typing on screen to retry.
  useEffect(() => {
    setReachOutAtInput(toDateTimeLocalInput(contact?.reachOutAt))
    setReachOutReasonInput(contact?.reachOutReason || '')
    setInstructionError('')
  }, [contact?.id, contact?.reachOutAt, contact?.reachOutReason])

  // Phone mirrors separately, and deliberately so. Folding it into the effect
  // above made saving a phone number wipe an in-progress reach-out
  // instruction: the phone save changes contact.phone, the parent re-supplies
  // the panel with the saved contact, and the shared effect then overwrote the
  // strategist's unsaved typing with the stored values.
  //
  // The panel is mounted once and handed a different contact as the selection
  // changes, so this cannot be an uncontrolled field — it would show the
  // previously selected contact's number.
  useEffect(() => {
    setPhoneInput(formatPhoneNumber(contact?.phone))
  }, [contact?.id, contact?.phone])

  if (!contact) return null

  const handleStatusChange = async (status: string) => {
    try {
      const updated = await contactsApi.update({ id: contact.id, status } as Contact & { id: string })
      onUpdate(updated)
      if (STATUSES_PROMPTING_REFERRAL_CHECK.has(status)) {
        setReferralPromptOpen(true)
      }
    } catch (error) {
      // The server validates the status vocabulary, so a rejected write has to
      // say so: the Select silently snapping back to its old value looks like
      // nothing happened.
      console.error('Failed to update status:', error)
      showError(error instanceof Error ? error.message : 'Failed to update status')
    }
  }

  // Saved on blur, and only when the number is complete — a half-typed
  // number is left on screen with its error rather than written and rejected.
  const handlePhoneBlur = async () => {
    const trimmed = phoneInput.trim()
    if (trimmed && !isValidPhone(trimmed)) return
    const nextPhone = trimmed ? toE164(trimmed) : ''
    if (nextPhone === (contact.phone || '')) return
    try {
      const updated = await contactsApi.update({
        id: contact.id,
        phone: nextPhone,
      } as Contact & { id: string })
      onUpdate(updated)
    } catch (error) {
      console.error('Failed to update phone:', error)
      showError('Failed to update phone number')
    }
  }

  const handleConnectionTypeChange = async (connectionType: string) => {
    try {
      const updated = await contactsApi.update({ id: contact.id, connectionType } as Contact & { id: string })
      onUpdate(updated)
    } catch (error) {
      console.error('Failed to update connection type:', error)
    }
  }

  const instructionDirty =
    reachOutAtInput !== toDateTimeLocalInput(contact.reachOutAt) ||
    reachOutReasonInput !== (contact.reachOutReason || '')

  const handleSaveInstruction = async () => {
    // datetime-local hands back '' for anything incomplete, so a non-empty
    // value that will not parse means the field was filled from somewhere
    // else. Say so rather than sending an Invalid Date to the API.
    const parsed = reachOutAtInput ? new Date(reachOutAtInput) : null
    if (parsed && Number.isNaN(parsed.getTime())) {
      setInstructionError('Enter a valid date and time.')
      return
    }

    setSavingInstruction(true)
    setInstructionError('')
    try {
      const updated = await contactsApi.update({
        id: contact.id,
        reachOutAt: parsed ? parsed.toISOString() : null,
        reachOutReason: reachOutReasonInput.trim(),
      })
      onUpdate(updated)
    } catch (error) {
      console.error('Failed to save reach-out instruction:', error)
      setInstructionError('Could not save the instruction. Your text is still here — try again.')
    } finally {
      setSavingInstruction(false)
    }
  }

  const handleLogInteraction = async () => {
    if (!newInteractionType) return
    setSaving(true)
    try {
      const interaction = await contactInteractionsApi.create({
        contactId: contact.id,
        type: newInteractionType,
        notes: newInteractionNotes,
        // Midday local, so a date-only value never lands on the previous day
        // once it is converted to UTC for anyone west of the meridian.
        occurredAt: new Date(`${newInteractionDate}T12:00:00`).toISOString(),
      })
      setInteractions((prev) => [interaction, ...prev])
      setNewInteractionNotes('')
      setNewInteractionType(DEFAULT_INTERACTION_TYPE)
      setNewInteractionDate(todayLocalYMD())
    } catch (error) {
      // A refused write used to reach only the console, while the form cleared
      // exactly as it does on success — so a rejected interaction looked like a
      // saved one and the note was simply gone. That is how the timezone defect
      // survived two QA passes. The server's own message is shown, because it
      // names the actual problem ("occurredAt cannot be in the future") where a
      // generic failure string would not.
      console.error('Failed to log interaction:', error)
      showError(error instanceof Error ? error.message : 'Failed to log interaction')
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    try {
      await contactsApi.delete(contact.id)
      onDelete(contact.id)
      setDeleteConfirmOpen(false)
      onClose()
    } catch (error) {
      console.error('Failed to delete contact:', error)
    }
  }

  const handleReferralYes = () => {
    setReferralPromptOpen(false)
    onAddReferral?.(contact.id, contact.fullName)
  }

  // The same gate the composer itself carries. A read-only panel is a record
  // to look at, so there is nothing to compose from it.
  const showOutreach = !readOnly

  return (
    <>
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullScreenBelow={768}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            display: 'flex',
            flexDirection: 'column',
            // Full height as a sheet, a tall dialog otherwise. 90vh on a phone
            // left a strip of backdrop the content could not use.
            maxHeight: isPhone ? '100%' : '90vh',
          },
        }}
      >
        {/* The identity block stays put while the body scrolls, so you always
            know whose record you are three sections deep into. */}
        <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              {/* This panel covers the page header that names the client, and
                  staff move between clients all day, so it restates whose
                  network the contact belongs to. A client reading their own
                  network already knows, hence the prop is optional. */}
              {clientName && (
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.6 }}
                >
                  {clientName}&rsquo;s network
                </Typography>
              )}
              <Typography
                component="h2"
                sx={{ fontSize: '1rem', lineHeight: '1.375rem', fontWeight: 600 }}
              >
                {contact.fullName}
              </Typography>
              {(contact.position || contact.company) && (
                <Typography variant="body2" color="text.secondary">
                  {[contact.position, contact.company].filter(Boolean).join(' — ')}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="Close contact details"
              sx={{ width: 44, height: 44, flexShrink: 0 }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Outreach used to be a second dialog stacked on top of this one, so
            reaching a template meant three surfaces deep: row, panel, modal.
            It is a view of this surface now. A portal client reaches it too —
            the composer is gated on readOnly, not on audience. */}
        {showOutreach ? (
          <Tabs
            value={view}
            onChange={(_, next) => setView(next as PanelView)}
            sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="record" label="Record" />
            <Tab value="outreach" label="Outreach" />
          </Tabs>
        ) : (
          <Divider />
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pt: 3, pb: 3 }}>
          {view === 'outreach' ? (
            <OutreachComposer
              recipient={recipientFromContact(contact)}
              // Networking outreach is a contact interaction, and the undo
              // handle is what Copy & Log's snackbar unwinds.
              onRecordOutreach={async (entry) => {
                const logged = await outreachLogApi.send({ contactId: contact.id, ...entry })
                return {
                  undo: async () => {
                    await outreachLogApi.undo(logged.id)
                  },
                }
              }}
              // A search-partner form letter is not something you send a
              // personal contact, and neither is a wild card cold intro
              // (MWA-357) — both belong to their own outreach tabs.
              categoryFilter={(slug) => slug !== 'search_partner' && slug !== 'wildcard_outreach'}
              clientTargetRole={clientTargetRole}
              onOutreachSent={handleOutreachChanged}
              onDone={() => setView('record')}
            />
          ) : (
            <>
              <PanelSection first>
                <Stack spacing={1.5}>
                  {contact.email && (
                    <Typography variant="body2" color="text.secondary">
                      {contact.email}
                    </Typography>
                  )}
                  {contact.linkedinUrl && (
                    <Box>
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        href={contact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn Profile
                      </Button>
                    </Box>
                  )}
                  {/* The SMS handoff in the Outreach view prefills its
                      recipient from this, so it has to be enterable somewhere.
                      Not staff-only: the composer is gated on readOnly rather
                      than audience, so a portal client reaches Send SMS too and
                      is the one who knows the number. Add Contact already lets
                      them set it on create. */}
                  {!readOnly && (
                    <TextField
                      label="Phone"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(formatPhoneInput(e.target.value))}
                      onBlur={handlePhoneBlur}
                      error={!!phoneInput.trim() && !isValidPhone(phoneInput)}
                      helperText={
                        phoneInput.trim() && !isValidPhone(phoneInput) ? 'Enter a 10-digit number' : ''
                      }
                      placeholder="(555) 123-4567"
                      size="small"
                      fullWidth
                    />
                  )}
                </Stack>
              </PanelSection>

              {/* Assignment — the strategist hands this person to the client on
                  a date and says why. The date records when the contact was
                  assigned, not a deadline for the client to act by: coaches
                  cannot dictate when a client actually reaches out (MWA-353).
                  Staff author it here; the client reads the same two values
                  below and cannot change them, which update-contact enforces
                  server-side however the write arrives. */}
              {audience === 'staff' ? (
                <PanelSection title="Contact Assignment">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}
                  >
                    The client sees this on their own Side Door page, alongside{' '}
                    {contact.firstName}. They can read it but not change it.
                  </Typography>
                  <Stack spacing={1.5} alignItems="flex-start">
                    <TextField
                      label="Assigned on"
                      type="datetime-local"
                      size="small"
                      fullWidth
                      value={reachOutAtInput}
                      onChange={(e) => setReachOutAtInput(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={savingInstruction}
                    />
                    <TextField
                      label="Reason for connecting"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={reachOutReasonInput}
                      onChange={(e) => setReachOutReasonInput(e.target.value)}
                      disabled={savingInstruction}
                      helperText="Why this person, in words the client can act on."
                    />
                    {instructionError && (
                      <Alert severity="error" sx={{ alignSelf: 'stretch' }}>
                        {instructionError}
                      </Alert>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveInstruction}
                      disabled={savingInstruction || !instructionDirty}
                    >
                      {savingInstruction ? 'Saving...' : 'Save Instruction'}
                    </Button>
                  </Stack>
                </PanelSection>
              ) : (
                (formatReachOutAt(contact.reachOutAt) || contact.reachOutReason) && (
                  <PanelSection title="Your Strategist&rsquo;s Instruction">
                    {formatReachOutAt(contact.reachOutAt) && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Assigned on {formatReachOutAt(contact.reachOutAt)}
                      </Typography>
                    )}
                    {contact.reachOutReason && (
                      <Typography variant="body2" color="text.secondary">
                        {contact.reachOutReason}
                      </Typography>
                    )}
                  </PanelSection>
                )
              )}

              {!readOnly && (
                <PanelSection title="Status">
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" fullWidth>
                      {/* shrink is the counterpart of displayEmpty below: the Select always renders
                          content, so the label must always be raised or it sits on top of it. */}
                      <InputLabel shrink>Status</InputLabel>
                      <Select
                        value={contact.status ?? ''}
                        label="Status"
                        // Without displayEmpty a Select holding '' renders blank, so the
                        // 145 contacts nobody has reached out to showed an empty Status
                        // field while the contacts table read "Not Yet Actioned".
                        displayEmpty
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        {/* A contact nobody has reached out to has no status. The
                            placeholder names that state rather than leaving the field
                            blank, and cannot be chosen — picking one of the three is
                            how staff record that an outreach happened. */}
                        <MenuItem value="" disabled>
                          {CONTACT_STATUS_UNSET_LABEL}
                        </MenuItem>
                        {/* A contact can still hold a status from an older vocabulary
                            that the migration deliberately left alone. Without its own
                            option the Select would render blank while the contacts
                            table shows the value — the panel would be the only place
                            claiming the contact has no status. */}
                        {contact.status &&
                          !CONTACT_STATUS_OPTIONS.some((opt) => opt.value === contact.status) && (
                            <MenuItem value={contact.status} disabled>
                              {contact.status.replace(/_/g, ' ')} (retired)
                            </MenuItem>
                          )}
                        {CONTACT_STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Connection</InputLabel>
                      <Select
                        value={contact.connectionType || DEFAULT_CONNECTION_TYPE}
                        label="Connection"
                        onChange={(e) => handleConnectionTypeChange(e.target.value)}
                      >
                        {CONNECTION_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </PanelSection>
              )}

              {/* The checklist renders its own section chrome. Wrapping it here
                  left a ruled band of blank space whenever it hid itself. */}
              {!readOnly && contact.clientId && (
                <OutreachChecklist
                  contact={contact}
                  contactStatus={contact.status}
                  contactId={contact.id}
                  clientId={contact.clientId}
                  onUpdate={onUpdate}
                />
              )}

              {!readOnly && (
                <PanelSection title="Log Interaction">
                  <Stack spacing={1.5} alignItems="flex-start">
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      label="Notes"
                      placeholder="What was said, and what happens next."
                      value={newInteractionNotes}
                      onChange={(e) => setNewInteractionNotes(e.target.value)}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ alignSelf: 'stretch' }}
                    >
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={newInteractionType}
                          label="Type"
                          onChange={(e) => setNewInteractionType(e.target.value)}
                        >
                          {INTERACTION_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {/* MWA-297 — defaults to today, so logging something as
                          it happens stays one click. Capped at today: an
                          interaction is a thing that happened, and the
                          forward-looking instruction lives on the contact. */}
                      <TextField
                        size="small"
                        type="date"
                        label="When"
                        value={newInteractionDate}
                        onChange={(e) => setNewInteractionDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: todayLocalYMD() }}
                        sx={{ minWidth: 150 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleLogInteraction}
                        disabled={saving}
                      >
                        {saving ? 'Logging...' : 'Log'}
                      </Button>
                    </Stack>
                  </Stack>
                </PanelSection>
              )}

              {/* MWA-297 — the referral prompt only fires when a status becomes
                  'contacted', but a referred job can come up in any
                  conversation. This is the standing way in. */}
              {onAddReferredOpportunity && (
                <PanelSection dense>
                  <Button
                    size="small"
                    startIcon={<WorkOutlineIcon />}
                    onClick={() => onAddReferredOpportunity(contact.id, contact.fullName)}
                  >
                    Add a job {contact.firstName} told {audience === 'staff' ? 'us' : 'you'} about
                  </Button>
                </PanelSection>
              )}

              <PanelSection title="Activity">
                {loadingInteractions ? (
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" height={44} />
                    <Skeleton variant="rounded" height={44} />
                    <Skeleton variant="rounded" height={44} />
                  </Stack>
                ) : interactionsError ? (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={loadInteractions}>
                        Retry
                      </Button>
                    }
                  >
                    Could not load this contact&rsquo;s activity.
                  </Alert>
                ) : activityRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet
                  </Typography>
                ) : (
                  <>
                    {followUpsError && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 1 }}
                        action={
                          <Button color="inherit" size="small" onClick={loadOutreach}>
                            Retry
                          </Button>
                        }
                      >
                        Could not load follow-up status. Activity is shown without it.
                      </Alert>
                    )}
                    <List dense disablePadding>
                      {activityRows.map(({ id, at, interaction, followUp }) => {
                        const state = followUp ? followUpState(followUp, Date.now()) : null
                        const pending = state === 'due' || state === 'overdue'
                        return (
                          <ListItem key={id} disablePadding sx={{ mb: 1, alignItems: 'flex-start' }}>
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                  <Chip label={interaction ? interactionTypeLabel(interaction.type) : 'Outreach sent'} size="small" />
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(at).toLocaleDateString()}
                                  </Typography>
                                  {followUp && state && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color={FOLLOW_UP_CHIP[state].color}
                                      label={FOLLOW_UP_CHIP[state].label(followUp)}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                              }
                              secondary={
                                interaction
                                  ? interaction.notes || undefined
                                  : followUp && (followUp.sentByName !== followUp.sentBy ? `Sent by ${followUp.sentByName}` : 'Sent by client')
                              }
                            />
                            {followUp && pending && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <Tooltip title="Mark followed up">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Mark followed up"
                                      disabled={savingFollowUpId === followUp.id}
                                      onClick={() => handleFollowUp(followUp.id, 'completed')}
                                    >
                                      <CheckCircleIcon fontSize="small" color="success" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Dismiss follow-up">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Dismiss follow-up"
                                      disabled={savingFollowUpId === followUp.id}
                                      onClick={() => handleFollowUp(followUp.id, 'dismissed')}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            )}
                          </ListItem>
                        )
                      })}
                    </List>
                  </>
                )}
              </PanelSection>

              {!readOnly && (
                <PanelSection>
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete Contact
                  </Button>
                </PanelSection>
              )}
            </>
          )}
        </Box>
      </ResponsiveSheet>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete {contact.fullName}. It cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Referral Prompt */}
      <Dialog open={referralPromptOpen} onClose={() => setReferralPromptOpen(false)}>
        <DialogTitle>Referral Check</DialogTitle>
        <DialogContent>
          <Typography>
            Did {contact.fullName} refer you to anyone?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReferralPromptOpen(false)}>No</Button>
          {/* MWA-297 — a referral is a person or a job, and this prompt is the
              moment the user is already thinking about it. Each action names
              what it adds rather than leaving the user to guess. */}
          <Button onClick={handleReferralYes} variant={onAddReferredOpportunity ? 'text' : 'contained'}>
            Yes — a person
          </Button>
          {onAddReferredOpportunity && (
            <Button
              variant="contained"
              onClick={() => {
                setReferralPromptOpen(false)
                onAddReferredOpportunity(contact.id, contact.fullName)
              }}
            >
              Yes — a job opening
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
